import { useState, type JSX } from 'react';
import { Button } from '@/components/ui/button.js';
import { Field } from '@/components/ui/field.js';
import { Input } from '@/components/ui/input.js';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet.js';
import { installUrlForShop } from './api-stores.js';

const SHOP_DOMAIN_PATTERN = /^[a-z0-9-]+\.myshopify\.com$/i;

/**
 * Installation d'une boutique (`02` §12.2, décision du 10/09/2026).
 *
 * Il n'y a plus de formulaire de création : on saisit le domaine
 * `xxx.myshopify.com` et on part vers Shopify. La boutique — code, nom,
 * devise — est **créée au retour du callback** à partir de ce que Shopify en
 * dit, puis on atterrit sur sa fiche.
 *
 * La redirection est une navigation plein navigateur (`window.location`), pas
 * une navigation du routeur : la cible est une route serveur, hors SPA.
 */
export interface StoreInstallSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function StoreInstallSheet({ open, onOpenChange }: StoreInstallSheetProps): JSX.Element {
  const [shopDomain, setShopDomain] = useState('');
  const [error, setError] = useState<string | null>(null);

  const trimmed = shopDomain.trim().toLowerCase();

  function submit(): void {
    if (!SHOP_DOMAIN_PATTERN.test(trimmed)) {
      setError('Domaine attendu : xxx.myshopify.com');
      return;
    }
    setError(null);
    window.location.assign(installUrlForShop(trimmed));
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Installer une boutique</SheetTitle>
          <SheetDescription>
            Saisissez le domaine Shopify de la boutique. Vous serez redirigé vers Shopify pour
            autoriser l’application ; la boutique sera créée automatiquement au retour, en pause.
          </SheetDescription>
        </SheetHeader>

        <form
          className="flex flex-col gap-4"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <Field label="Domaine Shopify" htmlFor="store-install-domain" error={error ?? undefined}>
            <Input
              id="store-install-domain"
              placeholder="miumlab-fr.myshopify.com"
              autoComplete="off"
              value={shopDomain}
              aria-invalid={error === null ? undefined : true}
              onChange={(event) => {
                setShopDomain(event.target.value);
                setError(null);
              }}
            />
          </Field>

          <SheetFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
              }}
            >
              Annuler
            </Button>
            <Button type="submit">Continuer vers Shopify</Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
